import { Alert, Button, classNames, Menu, toast } from '@cma/common'
import { PdfPreview } from '@cma/common/PdfPreview'
import { getAccessTokens } from '@cma/features/auth'
import { Branding } from '@cma/generated/graphql'
import { HelixIcon } from '@helix/helix-icon'
import {
  chevron_down as ChevronDownIcon,
  eye as EyeIcon,
  plus as PlusIcon,
  warning as WarningIcon
} from '@helix/helix-icon/outlined'
import { circle_info as CircleInfoIcon } from '@helix/helix-icon/solid'
import { useEffect, useMemo, useState } from 'react'
import AdminBrandEmptyState from './AdminBrandEmptyState'
import BrandAssetsForm from './BrandAssetsForm'
import { useBrandHub } from './BrandHubContext'
import { useDestroyBranding } from './useDestroyBranding'
import { useUpdateBranding } from './useUpdateBranding'

const brandActions = ['preview', 'publish', 'unpublish', 'delete'] as const
type ActionMode = (typeof brandActions)[number]

export type BrandingWithMeta = Omit<Branding, 'creator'> & {
  editable?: boolean
  inherited?: boolean
  creator?: { name: string } | null
}

export default function AdminBrandDetails() {
  const accessTokens = getAccessTokens()

  const [showTooltipBanner, setShowTooltipBanner] = useState(true)
  const [actionMode, setActionMode] = useState<ActionMode>('preview')
  const [showPreview, setShowPreview] = useState(false)
  const [showDelete, setShowDelete] = useState(false)

  const {
    selectedEntity,
    showAddBrandForm,
    platformClient,
    hasOfficePermissionForSelected,
    isSuperAdmin,
    setShowAddBrandForm,
    navigateToRoot
  } = useBrandHub()

  const { mutate: updateBranding, isLoading: isUpdating } = useUpdateBranding()

  const { mutate: destroyBranding, isLoading: isDestroying } =
    useDestroyBranding()

  const existingBranding = useMemo((): BrandingWithMeta | null => {
    if (!selectedEntity.id || !platformClient?.brandings) return null

    const brandingItem = platformClient.brandings.find(
      (item) =>
        item.brandableId === selectedEntity.id &&
        item.brandableType === selectedEntity.brandableType
    )

    if (!brandingItem?.branding) return null

    const branding = brandingItem.branding

    return {
      id: branding.id,
      name: branding.name,
      hasLogo: branding.hasLogo,
      published: branding.published,
      locked: branding.locked,
      logoFileName: branding.logoFileName,
      logoUrl: branding.logoUrl,
      secondaryLogoUrl: branding.secondaryLogoUrl,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
      primaryFont: branding.primaryFont,
      secondaryFont: branding.secondaryFont,
      brandableId: branding.brandableId,
      brandableType: branding.brandableType,
      createdBy: branding.createdBy,
      createdAt: branding.createdAt,
      updatedAt: branding.updatedAt,
      editable: brandingItem.editable,
      inherited: brandingItem.inherited,
      creator: branding.creator
        ? {
            name: branding.creator.name
          }
        : null
    }
  }, [
    selectedEntity.id,
    selectedEntity.brandableType,
    platformClient?.brandings
  ])

  const isDraftState = useMemo(() => {
    const hasExistingBranding = !!existingBranding
    const hasId = !!existingBranding?.id
    const hasCreatedBy = !!existingBranding?.createdBy

    if (!hasExistingBranding) return true
    return !(hasId && hasCreatedBy)
  }, [showAddBrandForm, existingBranding])

  const isChildEntity = useMemo(() => {
    return selectedEntity.brandableType !== 'Account'
  }, [selectedEntity.brandableType])

  const showEmptyState = useMemo(() => {
    return !existingBranding && !showAddBrandForm
  }, [existingBranding, showAddBrandForm])

  const shouldShowTooltip = useMemo(() => {
    return isChildEntity && existingBranding?.editable && showTooltipBanner
  }, [isChildEntity, existingBranding?.editable, showTooltipBanner])

  const canDeleteBranding = useMemo(() => {
    if (existingBranding?.inherited) return false

    if (isSuperAdmin) return true

    if (selectedEntity.brandableType === 'Account') return false

    if (
      ['Brokerage', 'Office', 'Team'].includes(selectedEntity.brandableType)
    ) {
      return hasOfficePermissionForSelected
    }

    return false
  }, [
    existingBranding?.inherited,
    isSuperAdmin,
    selectedEntity.brandableType,
    hasOfficePermissionForSelected
  ])

  useEffect(() => {
    setShowTooltipBanner(true)
    setActionMode('preview')
  }, [selectedEntity.id, existingBranding?.id])

  const handleDeleteBranding = () => {
    if (!existingBranding?.id) return

    const isRootBranding = selectedEntity.brandableType === 'Account'

    destroyBranding(
      { id: existingBranding.id },
      {
        onSuccess() {
          setShowDelete(false)

          if (isRootBranding) {
            setShowAddBrandForm(false)
            setShowTooltipBanner(true)
          } else {
            navigateToRoot()
          }
          setActionMode('preview')
        }
      }
    )
  }

  return (
    <>
      <div className="flex h-full min-h-[366px] w-[625px] flex-col items-center justify-center rounded-[6px] bg-gray-50 p-[24px]">
        <div className="helix-border-bottom mb-4 flex w-full items-center justify-between pb-[16px]">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <h2 className="helix-heading max-w-[336px] truncate">
                {selectedEntity.name}
              </h2>
              <span className="helix-badge helix-badge--inverted helix-badge--tiny">
                {selectedEntity.usersCount} Agents
              </span>
            </div>
            <p className="helix-small text-gray-500">
              {selectedEntity.displayAddress || 'No address available'}
            </p>
          </div>
          {showEmptyState ? (
            <Button
              size="sm"
              leftIcon={<HelixIcon icon={PlusIcon} className="mr-1 h-4 w-4" />}
              disabled={!hasOfficePermissionForSelected}
              onClick={() => setShowAddBrandForm(true)}>
              Add brand
            </Button>
          ) : (
            <div className="helix-btn-group">
              {actionMode === 'preview' && (
                <Button
                  disabled={!hasOfficePermissionForSelected || isDraftState}
                  leftIcon={<HelixIcon icon={EyeIcon} className="mr-1" />}
                  onClick={() => setShowPreview(true)}>
                  Preview
                </Button>
              )}
              {actionMode === 'delete' && (
                <Button
                  variant="danger"
                  disabled={!canDeleteBranding || isDraftState}
                  leftIcon={<HelixIcon icon={WarningIcon} className="mr-1" />}
                  onClick={() => {
                    if (canDeleteBranding && !isDraftState) setShowDelete(true)
                  }}>
                  Delete
                </Button>
              )}
              {(actionMode === 'publish' || actionMode === 'unpublish') && (
                <Button
                  loading={isUpdating}
                  disabled={!hasOfficePermissionForSelected || isDraftState}
                  onClick={() => {
                    if (existingBranding?.brandableId) {
                      updateBranding(
                        {
                          id: existingBranding.id,
                          input: {
                            published: actionMode === 'publish',
                            name: existingBranding.name
                          }
                        },
                        {
                          onSuccess() {
                            toast(
                              `${selectedEntity.name} has been ${
                                actionMode === 'publish'
                                  ? 'published'
                                  : 'unpublished'
                              }`,
                              {
                                variant: 'success'
                              }
                            )
                            setActionMode('preview')
                          },
                          onError(error) {
                            toast(`${error.message}`, {
                              variant: 'error'
                            })
                          }
                        }
                      )
                    }
                  }}>
                  {actionMode === 'publish'
                    ? isUpdating
                      ? 'Publishing'
                      : 'Publish'
                    : isUpdating
                    ? 'Unpublishing'
                    : 'Unpublish'}
                </Button>
              )}
              <Menu
                disabled={isDraftState}
                button={
                  <button
                    disabled={isDraftState}
                    className={classNames(
                      'helix-btn flex h-[36px] w-[36px] items-center justify-center',
                      {
                        'helix-btn--primary': actionMode !== 'delete',
                        'helix-btn--danger': actionMode === 'delete'
                      }
                    )}>
                    <HelixIcon
                      icon={ChevronDownIcon}
                      className="h-4 w-4 shrink-0"
                    />
                  </button>
                }>
                {brandActions
                  .filter((mode) => mode !== actionMode)
                  .filter((mode) => {
                    if (!existingBranding) return true

                    if (
                      (mode === 'publish' && existingBranding.published) ||
                      (mode === 'unpublish' && !existingBranding.published)
                    ) {
                      return false
                    }

                    return true
                  })
                  .map((mode) => (
                    <Menu.Item
                      key={mode}
                      as="button"
                      onClick={() => setActionMode(mode)}>
                      <div className="flex items-center">
                        <span
                          className={classNames('helix-text--capitalize', {
                            'helix-text-red-600': mode === 'delete'
                          })}>
                          {mode}
                        </span>
                      </div>
                    </Menu.Item>
                  ))}
              </Menu>
            </div>
          )}
        </div>
        {showEmptyState ? (
          <AdminBrandEmptyState />
        ) : (
          <div className="flex w-full flex-col">
            <h2 className="helix-display-x-small mb-6">Brand assets</h2>
            {shouldShowTooltip && (
              <div className="mb-6 flex items-center gap-2.5 bg-blue-50 p-4">
                <HelixIcon
                  icon={CircleInfoIcon}
                  className="helix-svg-fill--blue-600 h-[24px] w-[24px] shrink-0"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Brand assets have been applied to these brand assets. You can
                  edit and override them, and your changes won't affect the
                  parent {platformClient?.platformAccountName || 'brand'}. They
                  will only apply to {selectedEntity.name}.
                </span>
                <Button
                  type="button"
                  onClick={() => setShowTooltipBanner(false)}>
                  Got it
                </Button>
              </div>
            )}
            <BrandAssetsForm
              isDisabled={shouldShowTooltip}
              hasBrandPermissions={hasOfficePermissionForSelected}
              existingBranding={existingBranding}
            />
          </div>
        )}
      </div>
      <PdfPreview
        url={`${process.env.VITE_APP_CMA_URL}/api/cma_app/platform_report/preview.pdf?platform_entity_type=${existingBranding?.brandableType}&platform_entity_id=${existingBranding?.brandableId}&jwt=${accessTokens?.currentToken}`}
        showPreview={showPreview}
        onClose={() => setShowPreview(false)}
      />
      <Alert variant="danger" isOpen={showDelete}>
        <Alert.Title>Delete Brand</Alert.Title>
        <Alert.Content>
          Are you sure you want to delete this brand
          <br />(
          <strong className="font-medium text-gray-700">
            {existingBranding?.name || 'Unknown Brand'}
          </strong>
          )? This action cannot be undone.
        </Alert.Content>
        <Alert.Cancel onClick={() => setShowDelete(false)}>Cancel</Alert.Cancel>
        <Alert.Confirm loading={isDestroying} onClick={handleDeleteBranding}>
          Delete Forever
        </Alert.Confirm>
      </Alert>
    </>
  )
}
